import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, UserCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { getJson } from "@/lib/api";
import { postJson, useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { countryName, flagOf } from "@/data/profile";
import { AuthorLink, Avatar } from "@/components/community/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/** Follow / unfollow toggle shown on someone else's public profile. */
export const FollowButton = ({ p, onChange }) => {
  const { t } = useLang();
  const { member } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (!member) return navigate(`/entrar?next=${encodeURIComponent(`/u/${p.handle}`)}`);
    setBusy(true);
    try {
      const r = await postJson(`/u/${p.handle}/follow`, null);
      onChange(r);
      toast.success(t(r.is_following ? "follow.done" : "follow.undone", { name: p.name }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const Icon = busy ? Loader2 : p.is_following ? UserCheck : UserPlus;
  return (
    <button type="button" onClick={toggle} disabled={busy} className={`${p.is_following ? "btn-ghost" : "btn-primary"} group mt-6`} data-testid="follow-toggle-btn">
      <Icon size={15} className={busy ? "animate-spin" : ""} />
      {p.is_following ? <><span className="group-hover:hidden">{t("follow.on")}</span><span className="hidden group-hover:inline">{t("follow.off")}</span></> : t("follow.btn")}
    </button>
  );
};

/** Dialog listing a profile's followers or the people it follows. */
export const FollowList = ({ handle, rel, onClose }) => {
  const { t, lang } = useLang();
  const [list, setList] = useState(null);
  useEffect(() => {
    if (!rel) return;
    setList(null);
    getJson(`/u/${handle}/${rel}`).then((d) => setList(Array.isArray(d) ? d : []));
  }, [handle, rel]);
  return (
    <Dialog open={!!rel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[80vh] overflow-y-auto border-white/10 bg-[var(--card)] text-[var(--ink)]" data-testid="follow-list-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl">{rel && t(`prof.${rel}`)}</DialogTitle></DialogHeader>
        {list === null && <Loader2 className="mx-auto animate-spin" />}
        {list?.length === 0 && <p className="text-sm text-[var(--muted)]" data-testid="follow-list-empty">{t(`follow.empty.${rel}`)}</p>}
        {list?.map((a) => (
          <AuthorLink key={a.handle} author={a} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-white/5" testid={`follow-list-item-${a.handle}`}>
            <Avatar author={a} size={40} />
            <span className="min-w-0">
              <span className="block truncate font-semibold">{a.name}</span>
              {a.country && <span className="text-xs text-[var(--muted)]">{flagOf(a.country)} {countryName(a.country, lang)}</span>}
            </span>
          </AuthorLink>
        ))}
      </DialogContent>
    </Dialog>
  );
};
